import React, { useEffect, useMemo, useState, useId } from 'react';
import { RequiredIndicator } from '@/components/Accessibility/RequiredIndicator';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/fluent-adapter';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/fluent-adapter';
import { makeStyles, tokens, shorthands } from '@fluentui/react-components';
import {
  Dismiss24Regular as DismissIcon,
  Add24Regular as AddIcon,
  Warning24Regular as WarningIcon,
  Info24Regular as InfoIcon,
} from '@fluentui/react-icons';
import type { TypeOfServiceForm } from '../../store/types';
import {
  IDENTIFIER_PREVIEW_LIMIT,
  IDENTIFIER_TYPES,
  MAX_IDENTIFIERS,
  identifierTypeOptionMap,
} from '../../constants/identifierTypes';
import type { IdentifierFormValue } from '../../store/types';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import { validateIdentifierValue } from '../../utils/identifierValidation';
import type { IdentifierValidationResult } from '../../utils/identifierValidation';
import {
  parseIdentifierValues,
  buildIdentifierKey,
  validateDateRange,
  getTodayLocalDateString,
} from '../../utils/requestParametersHelpers';
import { OTHER_MICROSOFT_SERVICES } from '../../constants/otherMicrosoftServices';
import { useAnnouncer } from '@/hooks/useAnnouncer';

/**
 * Data categories that can be requested for USA domestic requests.
 * At least one must be selected (unless preservation request).
 */
const DATA_CATEGORIES = [
  { value: 'BasicSubscriberInformation', label: 'Basic Subscriber Information' },
  { value: 'AuthenticationLogs', label: 'Authentication Logs' },
  { value: 'TransactionalData', label: 'Transactional Data' },
  { value: 'ServiceTelemetry', label: 'Service Telemetry' },
  { value: 'Content', label: 'Content' },
  { value: 'Other', label: 'Other' },
] as const;

interface TypeOfServiceStepProps {
  formData: TypeOfServiceForm;
  onFormChange: (data: Partial<TypeOfServiceForm>) => void;
  errors?: Record<string, string>;
  /** Whether this is a preservation request (data categories not required for preservation) */
  isPreservationRequest?: boolean;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXL,
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  responsiveGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingVerticalXXL,
    '@media (min-width: 1024px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  cardHeader: {
    rowGap: tokens.spacingVerticalXS,
    ...shorthands.padding(0, 0, tokens.spacingVerticalS, 0),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
    flex: 1,
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
  },
  serviceList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    maxHeight: '320px',
    overflowY: 'auto',
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalXS),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground5,
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: tokens.spacingHorizontalS,
  },
  manualInputRow: {
    display: 'flex',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXS,
    columnGap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  manualServiceInput: {
    flex: '1 1 auto',
    minWidth: '0',
    '@media (min-width: 480px)': {
      minWidth: '240px',
    },
  },
  presetServiceSection: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  presetServiceList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  manualServiceSection: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  tagList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalS),
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
  },
  identifierControls: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    rowGap: tokens.spacingVerticalS,
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  previewPill: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    ...shorthands.padding(tokens.spacingVerticalXXS, tokens.spacingHorizontalS),
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: tokens.fontSizeBase200,
  },
  identifierList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    maxHeight: '260px',
    overflowY: 'auto',
  },
  identifierRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground5,
    columnGap: tokens.spacingHorizontalS,
  },
  identifierMeta: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
  },
  identifierTypeBadge: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  identifierValue: {
    fontFamily: '"Segoe UI", system-ui, sans-serif',
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase300,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXL),
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'center',
  },
  helperText: {
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXXS,
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
  },
  dataCategoryGrid: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  dateRangeInputs: {
    display: 'flex',
    gap: tokens.spacingHorizontalXL,
    flexWrap: 'wrap',
    width: '100%',
  },
  dateField: {
    flex: '1 1 auto',
    minWidth: '200px',
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXXS,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    backgroundColor: tokens.colorNeutralBackground4,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  otherDataCategoryInput: {
    marginTop: tokens.spacingVerticalS,
  },
  buttonMarginLeft: {
    marginLeft: tokens.spacingHorizontalS,
  },
  required: {
    color: tokens.colorStatusDangerForeground1,
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

const DEFAULT_IDENTIFIER_TYPE = IDENTIFIER_TYPES[0]?.value ?? '';

export const TypeOfServiceStep: React.FC<TypeOfServiceStepProps> = ({
  formData,
  onFormChange,
  errors = {},
}) => {
  const styles = useStyles();
  const servicesLabelId = useId();
  const dataCatTitleId = useId();
  const dataCatDescId = useId();
  const announce = useAnnouncer();
  const [manualInput, setManualInput] = useState('');
  const [manualServiceError, setManualServiceError] = useState<string | null>(null);
  const [identifierType, setIdentifierType] = useState<string>(DEFAULT_IDENTIFIER_TYPE);
  const [identifierInput, setIdentifierInput] = useState('');
  const [identifierError, setIdentifierError] = useState<string | null>(null);
  const [otherDataCategoryInput, setOtherDataCategoryInput] = useState('');
  const [otherDataCategoryError, setOtherDataCategoryError] = useState<string | null>(null);
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);

  // Re-validate whenever the form's date values change (covers re-entry into
  // the step with existing values - without this, the inline error wouldn't
  // surface until the user edited a field).
  useEffect(() => {
    setDateValidationError(
      validateDateRange(formData.requestStartDate ?? null, formData.requestEndDate ?? null),
    );
  }, [formData.requestStartDate, formData.requestEndDate]);

  // Data categories state
  const selectedDataCategories = useMemo(
    () => new Set(formData.dataCategories ?? []),
    [formData.dataCategories]
  );
  const isOtherDataCategorySelected = selectedDataCategories.has('Other');

  const selectedServiceIdsLookup = useMemo(
    () => new Set(formData.selectedServiceIds.map((service) => service.toLowerCase())),
    [formData.selectedServiceIds]
  );

  // The server caps the Services list — preset selections and manual entries combined — at
  // 50 (SubmitDomesticCaseRequestValidator, mirroring CMS). The cap enforced here is on that
  // same combined total, not on the manual list alone, so a preset selection plus manual
  // entries cannot build a payload the server would reject (PORT-PARITY).
  const MAX_SERVICES = 50;
  const MAX_OTHER_DATA_CATEGORIES = 50;
  const totalServiceCount =
    formData.selectedServiceIds.length + formData.manualServices.length;
  const serviceLimitReached = totalServiceCount >= MAX_SERVICES;

  const handleManualAdd = () => {
    const value = manualInput.trim();
    if (!value) {
      const message = 'Enter a Microsoft service name before selecting Add.';
      setManualServiceError(message);
      announce(message, 'assertive');
      return;
    }
    if (serviceLimitReached) {
      const message = `You can select at most ${MAX_SERVICES} services in total.`;
      setManualServiceError(message);
      announce(message, 'assertive');
      return;
    }
    if (formData.manualServices.some((service) => service.toLowerCase() === value.toLowerCase())) {
      setManualInput('');
      return;
    }
    setManualServiceError(null);
    onFormChange({ manualServices: [...formData.manualServices, value] });
    setManualInput('');
  };

  const removeManualService = (value: string) => {
    onFormChange({ manualServices: formData.manualServices.filter((item) => item !== value) });
  };

  // Bug 7361710: preset checkbox selections must route to selectedServiceIds
  // (the "Services" array in the payload), not manualServices ("OtherServices").
  // Manual / free-text entries are still added via handleManualAdd below.
  const updatePresetSelectedService = (serviceName: string, checked: boolean) => {
    const normalizedName = serviceName.trim();
    if (!normalizedName) {
      return;
    }

    const next = [...formData.selectedServiceIds];
    const existingIndex = next.findIndex(
      (service) => service.toLowerCase() === normalizedName.toLowerCase()
    );

    if (checked) {
      if (existingIndex === -1) {
        if (serviceLimitReached) {
          const message = `You can select at most ${MAX_SERVICES} services in total.`;
          setManualServiceError(message);
          announce(message, 'assertive');
          return;
        }
        next.push(normalizedName);
        onFormChange({ selectedServiceIds: next });
      }
    } else if (existingIndex !== -1) {
      next.splice(existingIndex, 1);
      onFormChange({ selectedServiceIds: next });
    }
  };

  const addIdentifiers = () => {
    setIdentifierError(null);

    const values = parseIdentifierValues(identifierInput);
    if (values.length === 0) {
      setIdentifierError('Enter at least one identifier value.');
      return;
    }

    const validationKind = selectedTypeConfig?.validation;

    const normalizedResults: IdentifierValidationResult[] = [];
    for (const value of values) {
      const result = validateIdentifierValue(validationKind, value);
      if (result.error) {
        setIdentifierError(result.error);
        return;
      }
      normalizedResults.push(result);
    }

    const currentCount = formData.identifiers.length;
    if (currentCount >= MAX_IDENTIFIERS) {
      setIdentifierError(`Maximum of ${MAX_IDENTIFIERS} identifiers reached.`);
      return;
    }

    const allowable = Math.max(0, MAX_IDENTIFIERS - currentCount);
    const nextIdentifiers = [...formData.identifiers];
    const existingKeys = new Set(
      formData.identifiers.map((identifier) =>
        buildIdentifierKey(identifier.type, identifier.value)
      )
    );

    let additions = 0;

    for (const { normalizedValue } of normalizedResults) {
      if (additions >= allowable) {
        break;
      }

      const key = buildIdentifierKey(identifierType, normalizedValue);
      if (existingKeys.has(key)) {
        continue;
      }

      const entry: IdentifierFormValue = {
        type: identifierType,
        value: normalizedValue,
      };

      nextIdentifiers.push(entry);
      existingKeys.add(key);
      additions += 1;
    }

    if (additions === 0) {
      setIdentifierError('All identifier values already exist.');
      return;
    }

    onFormChange({ identifiers: nextIdentifiers });
    setIdentifierInput('');
  };

  const removeIdentifier = (index: number) => {
    const next = formData.identifiers.filter((_, idx) => idx !== index);
    onFormChange({ identifiers: next });
  };

  const identifierPreview = useMemo(
    () => parseIdentifierValues(identifierInput),
    [identifierInput]
  );

  const selectedTypeConfig = identifierTypeOptionMap[identifierType];

  const handleIdentifierTypeChange = (value: string) => {
    setIdentifierType(value);
    setIdentifierError(null);
  };

  // Data category handlers
  const updateDataCategory = (category: string, checked: boolean) => {
    const next = new Set(selectedDataCategories);
    if (checked) {
      next.add(category);
    } else {
      next.delete(category);
      // If unchecking "Other", clear the other data categories
      if (category === 'Other') {
        onFormChange({ 
          dataCategories: Array.from(next),
          otherDataCategories: [],
        });
        setOtherDataCategoryInput('');
        setOtherDataCategoryError(null);
        return;
      }
    }
    onFormChange({ dataCategories: Array.from(next) });
  };

  const handleAddOtherDataCategory = () => {
    const value = otherDataCategoryInput.trim();
    if (!value) {
      const message = 'Enter a data category before selecting Add.';
      setOtherDataCategoryError(message);
      announce(message, 'assertive');
      return;
    }

    const currentOther = formData.otherDataCategories ?? [];
    if (currentOther.length >= MAX_OTHER_DATA_CATEGORIES) {
      return;
    }
    if (currentOther.some((c) => c.toLowerCase() === value.toLowerCase())) {
      setOtherDataCategoryInput('');
      return;
    }

    setOtherDataCategoryError(null);
    onFormChange({ otherDataCategories: [...currentOther, value] });
    setOtherDataCategoryInput('');
  };

  const removeOtherDataCategory = (value: string) => {
    const currentOther = formData.otherDataCategories ?? [];
    onFormChange({ otherDataCategories: currentOther.filter((c) => c !== value) });
  };

  const handleRequestStartDateChange = (value: string) => {
    const newStartDate = value || null;
    onFormChange({ requestStartDate: newStartDate });
    const error = validateDateRange(newStartDate, formData.requestEndDate);
    setDateValidationError(error);
  };

  const handleRequestEndDateChange = (value: string) => {
    const newEndDate = value || null;
    onFormChange({ requestEndDate: newEndDate });
    const error = validateDateRange(formData.requestStartDate, newEndDate);
    setDateValidationError(error);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.title}>Request Parameters</h2>
        <p className={styles.muted}>
          Specify the date range, services, data categories, and identifiers for your request.
        </p>
      </div>

      {/* Date Range Section - First */}
      <Card className={styles.card}>
          <CardHeader className={styles.cardHeader}>
            <CardTitle>Date Range</CardTitle>
            <CardDescription>
              Specify the overall date range for the data you are requesting.
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
            <div className={styles.dateRangeInputs}>
              <div className={styles.dateField}>
                <Label htmlFor="requestStartDate">Start Date<RequiredIndicator /></Label>
                <Input
                  id="requestStartDate"
                  type="date"
                  max={getTodayLocalDateString()}
                  value={formData.requestStartDate ?? ''}
                  onChange={(e) => handleRequestStartDateChange(e.target.value)}
                />
              </div>
              <div className={styles.dateField}>
                <Label htmlFor="requestEndDate">End Date<RequiredIndicator /></Label>
                <Input
                  id="requestEndDate"
                  type="date"
                  max={getTodayLocalDateString()}
                  value={formData.requestEndDate ?? ''}
                  onChange={(e) => handleRequestEndDateChange(e.target.value)}
                />
              </div>
            </div>

            {(dateValidationError || errors.requestDateRange) && (
              <span className={styles.errorText} data-testid="date-range-error">
                <WarningIcon />
                {dateValidationError || errors.requestDateRange}
              </span>
            )}
          </CardContent>
        </Card>

      {/* Microsoft Services & Identifiers - Side by side on large screens */}
      <div className={styles.responsiveGrid}>
        {/* Microsoft Services Section - Second */}
        <Card className={styles.card}>
          <CardHeader className={styles.cardHeader}>
            <CardTitle>Microsoft Services</CardTitle>
            <CardDescription>
              Select one or more Microsoft services for your request.
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
              <div className={styles.presetServiceSection}>
                <Label id={servicesLabelId}>Select One or More Microsoft Services</Label>
                <div className={styles.presetServiceList} role="group" aria-labelledby={servicesLabelId} data-field="microsoftServices">
                  {OTHER_MICROSOFT_SERVICES.map((service) => (
                    <Checkbox
                      key={service}
                      label={service}
                      checked={selectedServiceIdsLookup.has(service.toLowerCase())}
                      disabled={serviceLimitReached && !selectedServiceIdsLookup.has(service.toLowerCase())}
                      onCheckedChange={(checked) => updatePresetSelectedService(service, !!checked)}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.manualServiceSection}>
                <Label htmlFor="manualServiceInput">Add Another Microsoft Service</Label>
                <div className={styles.manualInputRow}>
                  <Input
                    id="manualServiceInput"
                    className={styles.manualServiceInput}
                    value={manualInput}
                    onChange={(e) => {
                      setManualInput(e.target.value);
                      if (manualServiceError) {
                        setManualServiceError(null);
                      }
                    }}
                    placeholder="Type a service and press Add"
                    maxLength={FIELD_LIMITS.MANUAL_SERVICE}
                    aria-invalid={(manualServiceError || errors.services || errors.manualServices) ? true : undefined}
                    aria-describedby={[
                      'manualServiceHelp',
                      manualServiceError && 'manualServiceError',
                      (errors.services || errors.manualServices) && 'manualServiceFieldError',
                    ].filter(Boolean).join(' ')}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleManualAdd();
                      }
                    }}
                  />
                  <Button type="button" icon={<AddIcon />} onClick={handleManualAdd} disabled={serviceLimitReached}>
                    Add
                  </Button>
                </div>
                <span id="manualServiceHelp" className={styles.helperText}>
                  {serviceLimitReached
                    ? `Maximum of ${MAX_SERVICES} services reached.`
                    : "Use this field if a service isn't listed above. Press Enter or Add to capture each value."}
                </span>
                {manualServiceError && (
                  <span id="manualServiceError" className={styles.errorText}>
                    <WarningIcon />
                    {manualServiceError}
                  </span>
                )}
                {formData.manualServices.length > 0 && (
                  <div className={styles.tagList}>
                    {formData.manualServices.map((service) => (
                      <span key={service} className={styles.tag}>
                        {service}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          icon={<DismissIcon />}
                          onClick={() => removeManualService(service)}
                          aria-label={`Remove ${service}`}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {(errors.services || errors.manualServices) && (
                <span id="manualServiceFieldError" className={styles.errorText}>
                  <WarningIcon />
                  {errors.services || errors.manualServices}
                </span>
              )}
            </CardContent>
          </Card>

        {/* Identifiers Section - Third */}
        <Card className={styles.card}>
          <CardHeader className={styles.cardHeader}>
            <CardTitle>Identifiers</CardTitle>
            <CardDescription>
              Provide at least one identifier that matches your selected services. Separate multiple entries with commas or new lines.
            </CardDescription>
            <div className={styles.infoBox}>
              <InfoIcon />
              <span>Individual identifier values are limited to 300 characters. For longer values or more than 50 identifiers, please upload an xls/xlsx file in the upload files step.</span>
            </div>
          </CardHeader>
          <CardContent className={styles.cardContent}>
          <div className={styles.identifierControls}>
              <div className={styles.formField}>
                <Label htmlFor="identifierType">Identifier Type</Label>
                <Select id="identifierType" aria-label="Identifier Type" value={identifierType} onValueChange={handleIdentifierTypeChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IDENTIFIER_TYPES.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className={styles.formField}>
                <Label htmlFor="identifierInput">Identifier Value(s)</Label>
                <Textarea
                  id="identifierInput"
                  value={identifierInput}
                  onChange={(e) => setIdentifierInput(e.target.value)}
                  placeholder={
                    selectedTypeConfig?.placeholder ??
                    'Enter identifier values'
                  }
                  rows={4}
                  maxLength={1024}
                />
                <span className={styles.helperText}>
                  {selectedTypeConfig?.example
                    ? `Examples: ${selectedTypeConfig?.example}`
                    : 'Separate multiple values with commas or new lines.'}
                </span>
              </div>

              {identifierPreview.length > 0 && (
                <div className={styles.tagList}>
                  {identifierPreview.slice(0, IDENTIFIER_PREVIEW_LIMIT).map((value) => (
                    <span key={value} className={styles.previewPill}>
                      {value}
                    </span>
                  ))}
                  {identifierPreview.length > IDENTIFIER_PREVIEW_LIMIT && (
                    <span className={styles.previewPill}>
                      +{identifierPreview.length - IDENTIFIER_PREVIEW_LIMIT} more
                    </span>
                  )}
                </div>
              )}

              <div>
                <Button type="button" icon={<AddIcon />} onClick={addIdentifiers}>
                  Add Identifier{identifierPreview.length > 1 ? 's' : ''}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className={styles.buttonMarginLeft}
                  onClick={() => {
                    setIdentifierInput('');
                    setIdentifierError(null);
                    onFormChange({ identifiers: [] });
                  }}
                >
                  Clear
                </Button>
              </div>
            </div>

            {identifierError && (
              <span className={styles.errorText} role="alert">
                <WarningIcon />
                {identifierError}
              </span>
            )}

            {errors.identifiers && (
              <span className={styles.errorText}>
                <WarningIcon />
                {errors.identifiers}
              </span>
            )}

            {formData.identifiers.length === 0 ? (
              <div className={styles.emptyState}>
                <WarningIcon />
                <span>No identifiers added yet</span>
                <small>Add at least one identifier to continue.</small>
              </div>
            ) : (
              <div className={styles.identifierList}>
                {formData.identifiers.map((identifier, index) => (
                  <div
                    key={`${identifier.type}-${identifier.value}-${index}`}
                    className={styles.identifierRow}
                  >
                    <div className={styles.identifierMeta}>
                      <span className={styles.identifierTypeBadge}>
                        {identifierTypeOptionMap[identifier.type]?.label ?? identifier.type}
                      </span>
                      <span className={styles.identifierValue}>
                        {identifier.value}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      icon={<DismissIcon />}
                      aria-label={`Remove ${identifier.value}`}
                      onClick={() => removeIdentifier(index)}
                    />
                  </div>
                ))}
              </div>
            )}

            <span className={styles.helperText}>
              {formData.identifiers.length}/{MAX_IDENTIFIERS} identifiers added
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Data Categories Section - Fourth */}
      <Card className={styles.card}>
          <CardHeader className={styles.cardHeader}>
            <CardTitle id={dataCatTitleId}>Data Categories</CardTitle>
            <CardDescription id={dataCatDescId}>
              Select at least one category of data you are requesting.
            </CardDescription>
          </CardHeader>
          <CardContent className={styles.cardContent}>
            <div className={styles.dataCategoryGrid} role="group" aria-labelledby={dataCatTitleId} aria-describedby={dataCatDescId} data-field="dataCategories">
            {DATA_CATEGORIES.map((category) => (
              <Checkbox
                key={category.value}
                label={category.label}
                checked={selectedDataCategories.has(category.value)}
                onCheckedChange={(checked) => updateDataCategory(category.value, !!checked)}
              />
            ))}
          </div>

          {/* Other Data Category Input */}
          {isOtherDataCategorySelected && (
            <div className={styles.otherDataCategoryInput}>
              <Label htmlFor="otherDataCategoryInput">Specify Other Data Category</Label>
              <div className={styles.manualInputRow}>
                <Input
                  id="otherDataCategoryInput"
                  className={styles.manualServiceInput}
                  value={otherDataCategoryInput}
                  onChange={(e) => {
                    setOtherDataCategoryInput(e.target.value);
                    if (otherDataCategoryError) {
                      setOtherDataCategoryError(null);
                    }
                  }}
                  placeholder="Enter specific data category"
                  maxLength={FIELD_LIMITS.DATA_CATEGORY_OTHER}
                  aria-invalid={(otherDataCategoryError || errors.otherDataCategories) ? true : undefined}
                  aria-describedby={[
                    otherDataCategoryError && 'otherDataCategoryError',
                    errors.otherDataCategories && 'otherDataCategoryFieldError',
                  ].filter(Boolean).join(' ') || undefined}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddOtherDataCategory();
                    }
                  }}
                />
                <Button type="button" icon={<AddIcon />} onClick={handleAddOtherDataCategory} disabled={(formData.otherDataCategories ?? []).length >= MAX_OTHER_DATA_CATEGORIES}>
                  Add
                </Button>
              </div>
              {otherDataCategoryError && (
                <span id="otherDataCategoryError" className={styles.errorText}>
                  <WarningIcon />
                  {otherDataCategoryError}
                </span>
              )}
              {(formData.otherDataCategories ?? []).length > 0 && (
                <div className={styles.tagList}>
                  {(formData.otherDataCategories ?? []).map((category) => (
                    <span key={category} className={styles.tag}>
                      {category}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        icon={<DismissIcon />}
                        onClick={() => removeOtherDataCategory(category)}
                        aria-label={`Remove ${category}`}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

            {errors.dataCategories && (
              <span className={styles.errorText}>
                <WarningIcon />
                {errors.dataCategories}
              </span>
            )}
            {errors.otherDataCategories && (
              <span id="otherDataCategoryFieldError" className={styles.errorText}>
                <WarningIcon />
                {errors.otherDataCategories}
              </span>
            )}
          </CardContent>
        </Card>
    </div>
  );
};
